import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { I18nPipe, ALAIN_I18N_TOKEN } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { AiffDetailComponent } from '../detail/aiff-detail.component';

// AIFF 数据接口
interface AiffSettingItem {
  id: string;
  displayType?: string;
  supportDevice?: string;
}

interface AiffItem {
  id: string;
  name: string;
  description: string;
  pictureId?: string;
  pictureUrl?: string;
  displayPictureUrl?: string;
  endPointUrl?: string;
  createTime: number;
  updateTime: number;
  items: AiffSettingItem[];
}

interface AiffDetailCloseResult {
  edited: boolean;
  refreshTime?: number;
}

@Component({
  selector: 'aiff-list',
  templateUrl: './aiff-list.component.html',
  styleUrls: ['./aiff-list.component.less'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    FormsModule,
    NzButtonModule,
    NzCardModule,
    NzInputModule,
    NzTableModule,
    NzTagModule,
    NzSelectModule,
    NzPopconfirmModule,
    NzIconModule,
    NzSpinModule,
    NzModalModule,
    NzDividerModule,
    NzDropDownModule,
    NzToolTipModule,
    NzAffixModule,
    I18nPipe,
    TestIdDirective
  ]
})
export class AiffListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() tenantId: string = '';
  @Input() pageTitleKey = 'tenant.detail.application-list';
  @Input() embeddedMode = false;
  @ViewChild('mobileScrollContainer') mobileScrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('mobileLoadMoreAnchor') mobileLoadMoreAnchor?: ElementRef<HTMLDivElement>;
  // 列表数据
  listOfData: AiffItem[] = [];
  // 加载状态
  loading = false;
  // 搜索关键词
  searchKeyword = '';
  // 分页数据
  // 分页数据接口
  pageIndex: number = 1;
  pageSize: number = 10;
  total: number = 0;
  refreshTime = Date.now();
  direction = 'desc';
  isMobileView = false;
  isLoadingMore = false;
  hasMoreMobile = false;
  hasNextPage = false;

  // 状态选项
  statusOptions: Array<{ label: string; value: string }> = [];
  private mobileLoadObserver?: IntersectionObserver;

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly ngZone = inject(NgZone);

  constructor(
    private readonly httpService: HttpService,
    private readonly message: NzMessageService,
    private readonly modal: NzModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly imageService: ImageService
  ) {}

  ngOnInit(): void {
    this.initStatusOptions();
    this.updateViewportMode();
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.syncMobileLoadObserver();
  }

  ngOnDestroy(): void {
    this.mobileLoadObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportMode(true);
  }

  private initStatusOptions(): void {
    this.statusOptions = [
      { label: this.i18n.fanyi('tenant.aiff.status.all'), value: '' },
      { label: this.i18n.fanyi('tenant.aiff.status.enabled'), value: 'active' },
      { label: this.i18n.fanyi('tenant.aiff.status.disabled'), value: 'inactive' }
    ];
  }
  // 加载数据
  private updateViewportMode(shouldReload = false): void {
    const nextMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
    const modeChanged = this.isMobileView !== nextMobile;
    this.isMobileView = nextMobile;

    if (modeChanged && shouldReload && this.tenantId) {
      this.pageIndex = 1;
      this.refreshTime = Date.now();
      this.loadData();
    } else {
      this.syncMobileLoadObserver();
    }
  }

  private updateMobileLoadState(): void {
    this.hasMoreMobile = this.isMobileView && this.hasNextPage;
    this.syncMobileLoadObserver();
  }

  private syncMobileLoadObserver(): void {
    this.mobileLoadObserver?.disconnect();

    if (!this.isMobileView || !this.hasMoreMobile || !this.mobileLoadMoreAnchor?.nativeElement) {
      return;
    }

    this.mobileLoadObserver = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ngZone.run(() => this.loadMoreOnMobile());
        }
      },
      {
        root: this.mobileScrollContainer?.nativeElement ?? null,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0.01
      }
    );

    this.mobileLoadObserver.observe(this.mobileLoadMoreAnchor.nativeElement);
  }

  loadData(options?: { append?: boolean; pageIndex?: number }): void {
    const append = !!options?.append;
    const requestPageIndex = options?.pageIndex ?? this.pageIndex;

    if (append) {
      this.isLoadingMore = true;
    } else {
      this.loading = true;
    }
    const params = {
      pageIndex: requestPageIndex - 1,
      pageSize: this.pageSize,
      key: this.searchKeyword,
      direction: this.direction,
      orderBy: 'updateTime',
      refreshTime: this.refreshTime,
      tenantId: this.tenantId
    };

    this.httpService
      .post(ConstantUtil.AIFF_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.isLoadingMore = false;
          this.cdr.detectChanges();
          this.syncMobileLoadObserver();
        })
      )
      .subscribe(res => {
        if (res.success) {
          const nextItems = Array.isArray(res.data) ? (res.data as AiffItem[]) : [];
          this.hasNextPage = nextItems.length === this.pageSize;

          if (!append && requestPageIndex > 1 && nextItems.length === 0) {
            this.pageIndex = requestPageIndex - 1;
            this.updateMobileLoadState();
            return;
          }

          this.pageIndex = requestPageIndex;
          this.listOfData = append ? [...this.listOfData, ...nextItems] : nextItems;
          this.total = this.listOfData.length;
          this.resolvePictureUrls(nextItems);
          this.updateMobileLoadState();
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('tenant.aiff.list.load.failed'));
          this.updateMobileLoadState();
        }
      });
  }

  loadMoreOnMobile(): void {
    if (!this.isMobileView || this.loading || this.isLoadingMore || !this.hasMoreMobile) {
      return;
    }

    this.loadData({
      append: true,
      pageIndex: this.pageIndex + 1
    });
  }

  // 搜索
  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  // 重置搜索
  resetSearch(): void {
    this.searchKeyword = '';
    this.search();
  }

  // 页码变更
  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadData();
  }

  // 每页条数变更
  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  previousPage(): void {
    if (this.pageIndex > 1) {
      this.onPageChange(this.pageIndex - 1);
    }
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.onPageChange(this.pageIndex + 1);
    }
  }

  getDisplayTypes(item: AiffItem): string[] {
    return [...new Set((item.items ?? []).map(setting => setting.displayType).filter((type): type is string => !!type))];
  }

  getDisplayTypeText(type: string): string {
    const typeMap: Record<string, string> = {
      Frame: this.i18n.fanyi('tenant.aiff.setting.display.type.frame'),
      AppList: this.i18n.fanyi('tenant.aiff.setting.display.type.applist'),
      Room: this.i18n.fanyi('tenant.aiff.setting.display.type.room'),
      ContactIndex: this.i18n.fanyi('tenant.aiff.setting.display.type.contact-index')
    };
    return typeMap[type] || type;
  }

  getDisplayTypeColor(type: string): string {
    const colorMap: Record<string, string> = {
      Frame: 'blue',
      AppList: 'green',
      Room: 'purple',
      ContactIndex: 'orange'
    };
    return colorMap[type] ?? 'default';
  }

  private resolvePictureUrls(items: AiffItem[]): void {
    items.forEach(item => {
      if (!item.pictureId) {
        item.displayPictureUrl = item.pictureUrl || '';
        return;
      }

      item.displayPictureUrl = '';
      this.imageService.loadImage(item.pictureId, 'S').subscribe(imageUrl => {
        item.displayPictureUrl = imageUrl || '';
        this.cdr.detectChanges();
      });
    });
  }

  // 删除AIFF
  deleteAiff(id: string): void {
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.aiff.delete.confirm.title'),
      nzContent: this.i18n.fanyi('tenant.aiff.delete.confirm.content'),
      nzWrapClassName: 'aiff-confirm-modal-shell',
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOnOk: () => {
        this.httpService.post(`${ConstantUtil.AIFF_DELETE}/${id}`, {}).subscribe(res => {
          if (res.success) {
            this.message.success(this.i18n.fanyi('tenant.aiff.delete.success'));
            this.refreshTime = Date.now();
            this.loadData();
          } else {
            showFallbackHttpError(this.message, res.msg, this.i18n.fanyi('tenant.aiff.delete.failed'));
          }
        });
      }
    });
  }
  addAiff(): void {
    const modalRef = this.modal.create({
      nzContent: AiffDetailComponent,
      nzWidth: '1200px',
      nzWrapClassName: 'aiff-detail-modal-shell',
      nzFooter: null,
      nzClosable: false,
      nzData: {
        isAdd: true,
        tenantId: this.tenantId // 传递 tenantId
      }
    });

    modalRef.afterClose.subscribe(result => {
      this.refreshAfterDetailClosed(result);
    });
  }

  // 修改编辑AIFF方法，使用模态框
  editAiff(id: string): void {
    this.modal
      .create({
        nzContent: AiffDetailComponent,
        nzWidth: '1200px',
        nzWrapClassName: 'aiff-detail-modal-shell',
        nzFooter: null,
        nzClosable: false,
        nzData: {
          aiffId: id,
          isAdd: false,
          tenantId: this.tenantId // 传递 tenantId
        }
      })
      .afterClose.subscribe(result => {
        this.refreshAfterDetailClosed(result);
      });
  }

  private refreshAfterDetailClosed(result: boolean | AiffDetailCloseResult | undefined): void {
    const didEdit = result === true || (typeof result === 'object' && result?.edited === true);
    if (!didEdit) {
      return;
    }

    this.pageIndex = 1;
    const parsedRefreshTime = typeof result === 'object' ? Number(result?.refreshTime) : NaN;
    this.refreshTime = Number.isFinite(parsedRefreshTime) && parsedRefreshTime > 0 ? parsedRefreshTime : Date.now();
    this.loadData();
  }
}
